import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { Snackbar, Alert, AlertColor } from '@mui/material'

interface Notification {
  message: string
  severity: AlertColor
  duration?: number
}

interface NotificationContextType {
  showSuccess: (message: string) => void
  showError: (message: string) => void
  showWarning: (message: string) => void
  showInfo: (message: string) => void
  showMessage: (message: string) => void
}

const NotificationContext = createContext<NotificationContextType | null>(null)

export function useNotification() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notification, setNotification] = useState<Notification | null>(null)
  const [simpleMessage, setSimpleMessage] = useState<string | null>(null)

  const showNotification = useCallback((message: string, severity: AlertColor, duration = 4000) => {
    setNotification({ message, severity, duration })
  }, [])

  const showSuccess = useCallback((message: string) => {
    showNotification(message, 'success')
  }, [showNotification])

  const showError = useCallback((message: string) => {
    showNotification(message, 'error', 6000)
  }, [showNotification])

  const showWarning = useCallback((message: string) => {
    showNotification(message, 'warning')
  }, [showNotification])

  const showInfo = useCallback((message: string) => {
    showNotification(message, 'info')
  }, [showNotification])

  // Mensagem simples (sem severity)
  const showMessage = useCallback((message: string) => {
    setSimpleMessage(message)
  }, [])

  const handleClose = useCallback(() => {
    setNotification(null)
  }, [])

  const handleCloseSimple = useCallback(() => {
    setSimpleMessage(null)
  }, [])

  return (
    <NotificationContext.Provider
      value={{ showSuccess, showError, showWarning, showInfo, showMessage }}
    >
      {children}

      {/* Alert notification */}
      <Snackbar
        open={!!notification}
        autoHideDuration={notification?.duration || 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleClose}
          severity={notification?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* Simple message snackbar */}
      <Snackbar
        open={!!simpleMessage}
        autoHideDuration={4000}
        onClose={handleCloseSimple}
        message={simpleMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </NotificationContext.Provider>
  )
}

export default NotificationContext
